import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { planningService } from '@/services/api/planning.service'
import type {
  ItemPlan,
  ItemPlanCreate,
  ItemPlanUpdate,
  PlanImplementacion,
  PlanImplementacionUpdate,
  SyncPreview,
  TipoItemPlan
} from '@/types/planning.types'
import {
  planningQueryKeys,
  planningQueryUtils
} from '@/utils/query/assessments/planningQueryClient.utils'

// =============================================================================
// QUERIES
// =============================================================================

export function usePlanData(assessmentId: string) {
  return useQuery({
    queryKey: planningQueryKeys.planning.plan(assessmentId),
    queryFn: () => planningService.getPlan(assessmentId),
    enabled: !!assessmentId
  })
}

export function usePlanItems(assessmentId: string, tipoItem?: TipoItemPlan) {
  return useQuery({
    queryKey: planningQueryKeys.planning.items(assessmentId),
    queryFn: () => planningService.getItems(assessmentId, tipoItem),
    enabled: !!assessmentId
  })
}

export function useBudgetSummary(assessmentId: string) {
  return useQuery({
    queryKey: planningQueryKeys.planning.budget(assessmentId),
    queryFn: () => planningService.getBudgetSummary(assessmentId),
    enabled: !!assessmentId
  })
}

export function useGanttData(assessmentId: string) {
  return useQuery({
    queryKey: planningQueryKeys.planning.gantt(assessmentId),
    queryFn: () => planningService.getGanttData(assessmentId),
    enabled: !!assessmentId
  })
}

// =============================================================================
// UPDATE PLAN
// =============================================================================

export interface UseUpdatePlanOptions {
  onSuccess?: (plan: PlanImplementacion) => void
  onError?: (error: Error) => void
}

export function useUpdatePlan(assessmentId: string, options: UseUpdatePlanOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PlanImplementacionUpdate) => planningService.updatePlan(assessmentId, data),

    onSuccess: async (plan) => {
      await planningQueryUtils.invalidatePlanningQueries(queryClient, assessmentId)
      options.onSuccess?.(plan)
    },

    onError: (error) => {
      options.onError?.(error as Error)
    },

    retry: 1,
    retryDelay: 1000
  })
}

// =============================================================================
// CREATE ITEM
// =============================================================================

export interface UseCreatePlanItemOptions {
  onSuccess?: (item: ItemPlan) => void
  onError?: (error: Error) => void
}

export function useCreatePlanItem(assessmentId: string, options: UseCreatePlanItemOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ItemPlanCreate) => planningService.createItem(assessmentId, data),

    onSuccess: async (item) => {
      await planningQueryUtils.invalidatePlanningQueries(queryClient, assessmentId)
      options.onSuccess?.(item)
    },

    onError: (error) => {
      options.onError?.(error as Error)
    },

    retry: 1,
    retryDelay: 1000
  })
}

// =============================================================================
// UPDATE ITEM
// =============================================================================

export interface UseUpdatePlanItemOptions {
  onSuccess?: (item: ItemPlan) => void
  onError?: (error: Error) => void
}

export function useUpdatePlanItem(assessmentId: string, options: UseUpdatePlanItemOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: ItemPlanUpdate }) =>
      planningService.updateItem(assessmentId, itemId, data),

    onSuccess: async (item) => {
      await planningQueryUtils.invalidatePlanningQueries(queryClient, assessmentId)
      options.onSuccess?.(item)
    },

    onError: (error) => {
      options.onError?.(error as Error)
    },

    retry: 1,
    retryDelay: 1000
  })
}

// =============================================================================
// DELETE ITEM
// =============================================================================

export interface UseDeletePlanItemOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export function useDeletePlanItem(assessmentId: string, options: UseDeletePlanItemOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (itemId: string) => planningService.deleteItem(assessmentId, itemId),

    onSuccess: async () => {
      await planningQueryUtils.invalidatePlanningQueries(queryClient, assessmentId)
      options.onSuccess?.()
    },

    onError: (error) => {
      options.onError?.(error as Error)
    },

    retry: 1,
    retryDelay: 1000
  })
}

// =============================================================================
// SYNC RECOMMENDATIONS
// =============================================================================

export interface UseSyncRecommendationsOptions {
  onSuccess?: (items: ItemPlan[]) => void
  onError?: (error: Error) => void
}

export function useSyncRecommendations(
  assessmentId: string,
  options: UseSyncRecommendationsOptions = {}
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (recomendacionIds?: string[]) =>
      planningService.syncRecommendations(assessmentId, recomendacionIds),

    onSuccess: async (items) => {
      await planningQueryUtils.invalidatePlanningQueries(queryClient, assessmentId)
      options.onSuccess?.(items)
    },

    onError: (error) => {
      options.onError?.(error as Error)
    },

    retry: 1,
    retryDelay: 1000
  })
}

// =============================================================================
// SYNC PREVIEW
// =============================================================================

export function useSyncPreview(assessmentId: string, enabled = false) {
  return useQuery<SyncPreview>({
    queryKey: planningQueryKeys.planning.syncPreview(assessmentId),
    queryFn: () => planningService.getSyncPreview(assessmentId),
    enabled: !!assessmentId && enabled
  })
}
